import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Container,
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  Button,
  Chip,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Snackbar,
  IconButton,
  List,
  Collapse,
  Divider,
  useTheme,
  useMediaQuery,
  MenuItem,
  Pagination,
  Select,
  FormControl,
  InputLabel,
  Stack,
  AlertTitle
} from '@mui/material';
import {
  Event,
  AccessTime,
  People,
  LocationOn,
  Euro,
  Message,
  Person,
  Business,
  CheckCircle,
  Cancel,
  Pending,
  Send,
  Reply,
  ExpandMore,
  FilterList,
  Payment,
  Close,
  ShoppingCart,
  DoneAll,
  Description, // ÚJ: Ikon a form válaszhoz
  InfoOutlined,
} from '@mui/icons-material';
import { alpha } from '@mui/material/styles';
import DialogHeader from '../Common/DialogHeader';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate, useLocation } from 'react-router-dom';
import bookingService from '../../services/bookingService';
import { format, parseISO } from 'date-fns';
import hu from 'date-fns/locale/hu';
import { useCart } from '../../contexts/CartContext';
import { PAYMENT_ENABLED } from '../../config/features';
import FormResponseViewer from '../Forms/FormResponseViewer';
import PaymentMethodDialog from '../Payment/PaymentMethodDialog';

// Státusz színek és ikonok (ugyanaz, mint a Dashboard.js-ben)
const getStatusColor = (status) => {
  switch (status) {
    case 'PENDING': return 'warning';
    case 'OFFER_SENT': return 'info';
    case 'OFFER_ACCEPTED': return 'primary';
    case 'OFFER_REJECTED': return 'error';
    case 'APPROVED': return 'success';
    case 'REJECTED': return 'error';
    case 'COMPLETED': return 'default';
    case 'CANCELLED': return 'default';
    default: return 'default';
  }
};

const getStatusText = (status) => {
  switch (status) {
    case 'PENDING': return 'Ajánlatot kért';
    case 'OFFER_SENT': return 'Ajánlatot kapott';
    case 'OFFER_ACCEPTED': return 'Ajánlat elfogadva';
    case 'OFFER_REJECTED': return 'Ajánlat visszautasítva';
    case 'APPROVED': return 'Jóváhagyott foglalás';
    case 'REJECTED': return 'Elutasított foglalás';
    case 'COMPLETED': return 'Befejezett';
    case 'CANCELLED': return 'Lemondott';
    default: return status;
  }
};

const getStatusIcon = (status) => {
  switch (status) {
    case 'PENDING': return <Pending />;
    case 'OFFER_SENT': return <Send />;
    case 'OFFER_ACCEPTED': return <CheckCircle />;
    case 'OFFER_REJECTED': return <Cancel />;
    case 'APPROVED': return <CheckCircle />;
    case 'REJECTED': return <Cancel />;
    case 'COMPLETED': return <CheckCircle />;
    case 'CANCELLED': return <Cancel />;
    default: return <Event />;
  }
};

// ÚJ: Fizetési státusz színek és szövegek
const getPaymentStatusColor = (status) => {
  switch (status) {
    case 'NOT_PAID': return 'error';
    case 'DEPOSIT_REQUESTED': return 'warning';
    case 'DEPOSIT_PAID': return 'info';
    case 'FULL_PAID': return 'success';
    case 'PENDING': return 'warning';
    case 'SUCCEEDED': return 'success';
    case 'FAILED': return 'error';
    case 'CANCELED':
    case 'CANCELLED': return 'default';
    default: return 'default';
  }
};

const getPaymentStatusText = (status) => {
  switch (status) {
    case 'NOT_PAID': return 'Nincs kifizetve';
    case 'DEPOSIT_REQUESTED': return 'Előleg szükséges';
    case 'DEPOSIT_PAID': return 'Előleg fizetve';
    case 'FULL_PAID': return 'Teljes összeg fizetve';
    case 'PENDING': return 'Fizetés folyamatban'; // Barion-tranzakció elindítva, még nem zárult le
    case 'SUCCEEDED': return 'Kifizetve';
    case 'FAILED': return 'Sikertelen fizetés';
    case 'CANCELED':
    case 'CANCELLED': return 'Fizetés megszakítva'; // a séma CANCELLED-et használ
    default: return status;
  }
};


const getPaymentMethodLabel = (method) => {
  switch (method) {
    case 'ON_SITE_CARD': return 'Helyszínen kártyával';
    case 'ON_SITE_CASH': return 'Helyszínen készpénzzel';
    case 'BANK_TRANSFER': return 'Online utalással';
    case 'BARION': return 'Barion';
    default: return method;
  }
};

// ÚJ KOMPONENS: BookingsList
// statusFilterRequest: { status, key } — a dashboard áttekintő sávja ezzel szűr egy státuszra
// onBookingsChanged: minden sikeres (újra)töltés után hívódik, hogy a dashboard számlálói frissüljenek
const BookingsList = ({ isDashboardView = false, statusFilterRequest = null, onBookingsChanged }) => {
  const { user, isClient, isProvider, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { addToCart, removeFromCart, isInCart } = useCart();
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedBookingId, setExpandedBookingId] = useState(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [filterStatus, setFilterStatus] = useState('ALL');
  const [filterDateFrom, setFilterDateFrom] = useState('');
  const [filterDateTo, setFilterDateTo] = useState('');
  const [filterOpen, setFilterOpen] = useState(false);
  const listTopRef = useRef(null);
  const onBookingsChangedRef = useRef(onBookingsChanged);
  onBookingsChangedRef.current = onBookingsChanged;

  // Kívülről kért státusz-szűrő (pl. „2 ajánlatkérés vár válaszra” kattintás)
  useEffect(() => {
    if (!statusFilterRequest) return;
    setPage(1);
    setFilterStatus(statusFilterRequest.status);
    setFilterDateFrom('');
    setFilterDateTo('');
    setFilterOpen(true);
  }, [statusFilterRequest]);

  // Ajánlat-dialógus: a mezőhibák az első mentési kísérlet után látszanak
  const [offerSubmitAttempted, setOfferSubmitAttempted] = useState(false);
  const [newOfferSubmitAttempted, setNewOfferSubmitAttempted] = useState(false);

  // Dialog állapotok
  const [sendOfferDialogOpen, setSendOfferDialogOpen] = useState(false);
  const [requestNewOfferDialogOpen, setRequestNewOfferDialogOpen] = useState(false);
  const [rejectOfferDialogOpen, setRejectOfferDialogOpen] = useState(false);
  const [cancelBookingDialogOpen, setCancelBookingDialogOpen] = useState(false);
  const [markCompletedDialogOpen, setMarkCompletedDialogOpen] = useState(false); // ÚJ: Befejezett dialog
  const [selectedBookingForAction, setSelectedBookingForAction] = useState(null);

  // ÚJ: Form válasz megtekintése dialog
  const [formResponseViewerOpen, setFormResponseViewerOpen] = useState(false);
  const [formResponseIdToView, setFormResponseIdToView] = useState(null);

  // Form adatok
  const [offerPrice, setOfferPrice] = useState('');
  const [offerMessage, setOfferMessage] = useState('');
  const [offerExpiresAt, setOfferExpiresAt] = useState('');
  const [offerDepositAmount, setOfferDepositAmount] = useState('');
  const [offerDepositDeadline, setOfferDepositDeadline] = useState('');
  const [offerFullPaymentDeadline, setOfferFullPaymentDeadline] = useState('');
  const [requestNewOfferMessage, setRequestNewOfferMessage] = useState('');
  const [requestNewOfferPrice, setRequestNewOfferPrice] = useState('');
  const [rejectOfferReason, setRejectOfferReason] = useState('');
  const [cancelBookingReason, setCancelBookingReason] = useState('');
  const [markCompletedNotes, setMarkCompletedNotes] = useState('');

  // Cart snackbar state
  const [cartSnackbarOpen, setCartSnackbarOpen] = useState(false);
  const [confirmPaymentDialog, setConfirmPaymentDialog] = useState({ open: false, bookingId: null, role: 'CLIENT', step: 'FULL' });
  const [paymentMethodDialog, setPaymentMethodDialog] = useState({ open: false, bookings: [], step: 'FULL' });
  const [confirmPaymentLoading, setConfirmPaymentLoading] = useState(false);

  const [actionLoading, setActionLoading] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');
  const [snackbarDetail, setSnackbarDetail] = useState('');


  // URL paraméterből érkező bookingId kezelése
  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const bookingIdFromUrl = queryParams.get('bookingId');
    if (bookingIdFromUrl) {
      setExpandedBookingId(parseInt(bookingIdFromUrl));
      // Eltávolítjuk a bookingId-t az URL-ből, hogy ne maradjon ott
      navigate(location.pathname, { replace: true });
    }
  }, [location.search, location.pathname, navigate]);

  // State-ből érkező bookingId kezelése (értesítésekből navigáláskor)
  useEffect(() => {
    if (location.state?.scrollToBooking) {
      setExpandedBookingId(location.state.scrollToBooking);
    }
  }, [location.state?.scrollToBooking]);

  const fetchBookings = useCallback(async () => {
    if (!isAuthenticated) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const response = await bookingService.getBookings({
        status: filterStatus,
        dateFrom: filterDateFrom || undefined,
        dateTo: filterDateTo || undefined,
        page,
        limit: 4,
        include: 'provider.user,formResponse'
      });
      if (response.success) {
        setBookings(response.data);
        setTotalPages(response.pagination?.pages ?? 1);
        onBookingsChangedRef.current?.();
      } else {
        setError(response.message || 'Foglalások betöltése sikertelen.');
      }
    } catch (err) {
      console.error('Error fetching bookings:', err);
      setError('Hiba történt a foglalások betöltése során.');
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated, page, filterStatus, filterDateFrom, filterDateTo]);

  useEffect(() => {
    fetchBookings();
  }, [fetchBookings]);

  const handleExpandClick = (bookingId) => {
    setExpandedBookingId(expandedBookingId === bookingId ? null : bookingId);
  };

  const showSnackbar = (message, severity = 'success', detail = '') => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarDetail(detail);
    setSnackbarOpen(true);
  };

  // --- Dialog kezelő függvények ---
  const handleOpenSendOfferDialog = (booking) => {
    setSelectedBookingForAction(booking);
    setOfferPrice(booking.estimatedPrice || '');
    setOfferMessage('');
    setOfferExpiresAt('');
    setOfferDepositAmount('');
    setOfferDepositDeadline('');
    setOfferFullPaymentDeadline('');
    setOfferSubmitAttempted(false);
    setSendOfferDialogOpen(true);
  };

  const handleOpenRequestNewOfferDialog = (booking) => {
    setSelectedBookingForAction(booking);
    setRequestNewOfferMessage('');
    setRequestNewOfferPrice('');
    setNewOfferSubmitAttempted(false);
    setRequestNewOfferDialogOpen(true);
  };

  const handleOpenRejectOfferDialog = (booking) => {
    setSelectedBookingForAction(booking);
    setRejectOfferReason('');
    setRejectOfferDialogOpen(true);
  };

  const handleOpenCancelBookingDialog = (booking) => {
    setSelectedBookingForAction(booking);
    setCancelBookingReason('');
    setCancelBookingDialogOpen(true);
  };

  const handleOpenMarkCompletedDialog = (booking) => { // ÚJ: Befejezett dialog megnyitása
    setSelectedBookingForAction(booking);
    setMarkCompletedNotes('');
    setMarkCompletedDialogOpen(true);
  };

  // ÚJ: Form válasz megtekintése
  const handleViewFormResponse = (formResponseId) => {
    setFormResponseIdToView(formResponseId);
    setFormResponseViewerOpen(true);
  };

  // Ajánlat mezőinek ellenőrzése (a backend ezeket nem vizsgálja — pl. előleg > ár is átmenne)
  const todayStr = new Date().toISOString().split('T')[0];
  const eventDateStr = selectedBookingForAction?.eventDate
    ? String(selectedBookingForAction.eventDate).split('T')[0]
    : null;
  const offerErrors = {};
  {
    const price = Number(offerPrice);
    const deposit = Number(offerDepositAmount);
    if (offerPrice === '' || offerPrice == null) offerErrors.price = 'Az ár megadása kötelező.';
    else if (!(price > 0)) offerErrors.price = 'Pozitív összeget adjon meg.';
    if (offerDepositAmount !== '' && offerDepositAmount != null) {
      if (!(deposit > 0)) offerErrors.deposit = 'Pozitív összeget adjon meg, vagy hagyja üresen.';
      else if (price > 0 && deposit >= price) offerErrors.deposit = 'Az előleg legyen kisebb az ajánlott árnál.';
      if (!offerDepositDeadline) offerErrors.depositDeadline = 'Előleg esetén a határidő kötelező.';
    }
    if (offerDepositDeadline && offerFullPaymentDeadline && offerDepositDeadline > offerFullPaymentDeadline) {
      offerErrors.depositDeadline = 'Az előleg határideje nem lehet a teljes összegé után.';
    }
    if (eventDateStr && offerFullPaymentDeadline && offerFullPaymentDeadline > eventDateStr) {
      offerErrors.fullDeadline = 'A határidő ne legyen az esemény napja után.';
    }
    if (eventDateStr && offerExpiresAt && offerExpiresAt > eventDateStr) {
      offerErrors.expiresAt = 'Az ajánlat ne legyen érvényes az esemény napja után.';
    }
  }
  const offerFieldError = (key) => (offerSubmitAttempted ? offerErrors[key] : undefined);

  const newOfferError = !requestNewOfferMessage.trim() && !requestNewOfferPrice
    ? 'Adjon meg javasolt összeget vagy üzenetet.'
    : (requestNewOfferPrice !== '' && !(Number(requestNewOfferPrice) > 0) ? 'Pozitív összeget adjon meg.' : null);

  // --- API hívások ---
  const handleSendOffer = async () => {
    setOfferSubmitAttempted(true);
    if (Object.keys(offerErrors).length > 0) return;
    setActionLoading(true);
    try {
      const response = await bookingService.sendOffer(
        selectedBookingForAction.id,
        offerPrice,
        offerMessage,
        offerExpiresAt,
        offerDepositAmount || null,
        offerDepositDeadline || null,
        offerFullPaymentDeadline || null,
      );
      if (response.success) {
        showSnackbar('Ajánlat elküldve!', 'success', 'Az ügyfelet értesítettük az ajánlatáról — hamarosan visszajelzést küld.');
        fetchBookings();
        setSendOfferDialogOpen(false);
        setSelectedBookingForAction(null);
      } else {
        showSnackbar(response.message || 'Hiba történt az ajánlat elküldése során.', 'error');
      }
    } catch (err) {
      console.error('Ajánlat elküldése hiba:', err);
      if (err.details && Array.isArray(err.details)) {
        const errorMessages = err.details.map(e => e.msg).join(', ');
        showSnackbar(`Validációs hiba: ${errorMessages}`, 'error');
      } else {
        showSnackbar(err.message || 'Ajánlat elküldése sikertelen.', 'error');
      }
    } finally {
      setActionLoading(false);
    }
  };

  const handleAcceptOffer = async (bookingId) => {
    setActionLoading(true);
    try {
      const response = await bookingService.acceptOffer(bookingId);
      if (response.success) {
        showSnackbar('Ajánlat elfogadva!', 'success', 'Várjon, amíg a szolgáltató jóváhagyja a foglalását és lefoglalja önnek az időpontot a naptárában.');
        fetchBookings();
      } else {
        showSnackbar(response.message || 'Hiba történt az ajánlat elfogadása során.', 'error');
      }
    } catch (err) {
      console.error('Ajánlat elfogadása hiba:', err);
      showSnackbar(err.message || 'Ajánlat elfogadása sikertelen.', 'error');
    } finally {
      setActionLoading(false);
    }
  };

  const handleRejectOffer = async () => {
    setActionLoading(true);
    try {
      const response = await bookingService.rejectOffer(selectedBookingForAction.id, rejectOfferReason);
      if (response.success) {
        showSnackbar('Ajánlat visszautasítva', 'info', 'A szolgáltató értesítve lett az elutasításról.');
        fetchBookings();
      } else {
        showSnackbar(response.message || 'Hiba történt az ajánlat visszautasítása során.', 'error');
      }
    } catch (err) {
      console.error('Ajánlat visszautasítása hiba:', err);
      showSnackbar(err.message || 'Ajánlat visszautasítása sikertelen.', 'error');
    } finally {
      setActionLoading(false);
      setRejectOfferDialogOpen(false);
      setSelectedBookingForAction(null);
    }
  };

  const handleRequestNewOffer = async () => {
    setNewOfferSubmitAttempted(true);
    if (newOfferError) return;
    setActionLoading(true);
    try {
      const response = await bookingService.requestNewOffer(
        selectedBookingForAction.id,
        requestNewOfferMessage,
        requestNewOfferPrice || null,
      );
      if (response.success) {
        showSnackbar('Ellenajánlat elküldve!', 'success', 'A szolgáltató hamarosan megküldi módosított ajánlatát.');
        fetchBookings();
      } else {
        showSnackbar(response.message || 'Hiba történt az új ajánlatkérés során.', 'error');
      }
    } catch (err) {
      console.error('Új ajánlatkérés hiba:', err);
      showSnackbar(err.message || 'Új ajánlatkérés sikertelen.', 'error');
    } finally {
      setActionLoading(false);
      setRequestNewOfferDialogOpen(false);
      setRequestNewOfferPrice('');
      setSelectedBookingForAction(null);
    }
  };

  const handleApproveBooking = async (bookingId) => {
    setActionLoading(true);
    try {
      const response = await bookingService.updateBookingStatus(bookingId, 'APPROVED');
      if (response.success) {
        showSnackbar('Foglalás jóváhagyva!', 'success', 'Az ügyfelet értesítettük, és az időpontot lefoglaltuk a naptárában.');
        fetchBookings();
      } else {
        showSnackbar(response.message || 'Hiba történt a foglalás jóváhagyása során.', 'error');
      }
    } catch (err) {
      console.error('Foglalás jóváhagyása hiba:', err);
      showSnackbar(err.message || 'Foglalás jóváhagyása sikertelen.', 'error');
    } finally {
      setActionLoading(false);
    }
  };

  const handleCancelBooking = async () => {
    setActionLoading(true);
    try {
      const response = await bookingService.updateBookingStatus(selectedBookingForAction.id, 'CANCELLED', cancelBookingReason);
      if (response.success) {
        showSnackbar('Foglalás lemondva', 'info');
        fetchBookings();
      } else {
        showSnackbar(response.message || 'Hiba történt a foglalás lemondása során.', 'error');
      }
    } catch (err) {
      console.error('Foglalás lemondása hiba:', err);
      showSnackbar(err.message || 'Foglalás lemondása sikertelen.', 'error');
    } finally {
      setActionLoading(false);
      setCancelBookingDialogOpen(false);
      setSelectedBookingForAction(null);
    }
  };

  const handleMarkAsCompleted = async () => { // ÚJ: Befejezetté jelölés
    setActionLoading(true);
    try {
      const response = await bookingService.updateBookingStatus(selectedBookingForAction.id, 'COMPLETED', markCompletedNotes); // ÚJ service metódus
      if (response.success) {
        showSnackbar('Foglalás teljesítve jelölve', 'success');
        fetchBookings();
      } else {
        showSnackbar(response.message || 'Hiba történt a foglalás befejezetté jelölése során.', 'error');
      }
    } catch (err) {
      console.error('Foglalás befejezetté jelölése hiba:', err);
      showSnackbar(err.message || 'Foglalás befejezetté jelölése sikertelen.', 'error');
    } finally {
      setActionLoading(false);
      setMarkCompletedDialogOpen(false);
      setSelectedBookingForAction(null);
    }
  };


  // Kosárba adás/eltávolítás kezelő
  const handleToggleCart = (bookingId) => {
    if (isInCart(bookingId)) {
      removeFromCart(bookingId);
      showSnackbar('Foglalás eltávolítva a kosárból.', 'info');
    } else {
      addToCart(bookingId);
      showSnackbar('Foglalás hozzáadva a kosárhoz.', 'success');
    }
  };

  // Offline fizetés megerősítése
  const handleConfirmPayment = async () => {
    setConfirmPaymentLoading(true);
    try {
      const token = localStorage.getItem('token');
      const { bookingId, role, step } = confirmPaymentDialog;
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/bookings/${bookingId}/confirm-payment`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({ role, step }),
        }
      );
      const data = await response.json();
      if (data.success) {
        showSnackbar(role === 'CLIENT' ? 'Fizetés sikeresen megerősítve!' : 'Fizetés beérkezése megerősítve!', 'success');
        fetchBookings();
        setConfirmPaymentDialog({ open: false, bookingId: null, role: 'CLIENT', step: 'FULL' });
      } else {
        showSnackbar(data.message || 'Hiba történt a megerősítés során.', 'error');
      }
    } catch (err) {
      showSnackbar('Hiba a megerősítés során.', 'error');
    } finally {
      setConfirmPaymentLoading(false);
    }
  };

  if (!isAuthenticated) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Alert severity="info">A foglalások megtekintéséhez be kell jelentkeznie.</Alert>
        <Button variant="contained" onClick={() => navigate('/login')} sx={{ mt: 2 }}>Bejelentkezés</Button>
      </Container>
    );
  }

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', minHeight: 300 }}>
        <CircularProgress />
        <Typography sx={{ ml: 2 }}>Foglalások betöltése...</Typography>
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }

  if (bookings.length === 0) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography variant="h6" gutterBottom>Nincsenek foglalások vagy ajánlatkérések.</Typography>
        {isClient() && (
          <Button variant="contained" onClick={() => navigate('/szolgaltatok')} sx={{ mt: 2 }}>
            Szolgáltatók keresése
          </Button>
        )}
      </Box>
    );
  }


  const statusOptions = [
    { value: 'ALL', label: 'Minden státusz' },
    { value: 'PENDING', label: getStatusText('PENDING') },
    { value: 'OFFER_SENT', label: getStatusText('OFFER_SENT') },
    { value: 'OFFER_ACCEPTED', label: getStatusText('OFFER_ACCEPTED') },
    { value: 'OFFER_REJECTED', label: getStatusText('OFFER_REJECTED') },
    { value: 'APPROVED', label: getStatusText('APPROVED') },
    { value: 'REJECTED', label: getStatusText('REJECTED') },
    { value: 'COMPLETED', label: getStatusText('COMPLETED') },
    { value: 'CANCELLED', label: getStatusText('CANCELLED') },
  ];

  const hasActiveFilter = filterStatus !== 'ALL' || filterDateFrom !== '' || filterDateTo !== '';

  return (
    <Box ref={listTopRef} sx={{ width: '100%', scrollMarginTop: '90px' }}>
      {/* Ha nem dashboard nézetben van, akkor megjelenítjük a címet */}
      {!isDashboardView && (
        <Typography variant="h4" gutterBottom sx={{ mb: 3 }}>
          {isClient() ? 'Ajánlatkéréseim és Foglalásaim' : 'Beérkezett Ajánlatkérések és Foglalások'}
        </Typography>
      )}

      {/* Szűrő gomb + panel */}
      <Box sx={{ mb: 2 }}>
        {/* Szűrők gomb sor */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Button
            size="small"
            variant={filterOpen ? 'contained' : 'outlined'}
            startIcon={<FilterList />}
            onClick={() => setFilterOpen(prev => !prev)}
            sx={{ borderRadius: '12px' }}
          >
            Szűrők
            {hasActiveFilter && ` (${[filterStatus !== 'ALL', filterDateFrom !== '', filterDateTo !== ''].filter(Boolean).length})`}
          </Button>
          {hasActiveFilter && (
            <Button
              size="small"
              variant="text"
              onClick={() => { setPage(1); setFilterStatus('ALL'); setFilterDateFrom(''); setFilterDateTo(''); }}
            >
              × Törlés
            </Button>
          )}
        </Box>

        {/* Szűrő panel */}
        <Collapse in={filterOpen}>
          <Box sx={{
            mt: 1.5,
            p: 2,
            borderRadius: '16px',
            border: `1px solid ${alpha(theme.palette.divider, 0.8)}`,
            background: alpha(theme.palette.background.paper, 0.8),
            '& .MuiOutlinedInput-root': { borderRadius: '12px' },
            '& .MuiInputLabel-root': { fontSize: '0.875rem' },
          }}>
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={1.5}
              alignItems={{ xs: 'stretch', sm: 'center' }}
              flexWrap="wrap"
            >
              <FormControl size="small" sx={{ minWidth: { xs: '100%', sm: 190 } }}>
                <InputLabel>Státusz</InputLabel>
                <Select
                  value={filterStatus}
                  label="Státusz"
                  onChange={(e) => { setPage(1); setFilterStatus(e.target.value); }}
                >
                  {statusOptions.map(opt => (
                    <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>

              <TextField
                size="small"
                type="date"
                label="Esemény dátuma (-tól)"
                value={filterDateFrom}
                onChange={(e) => { setPage(1); setFilterDateFrom(e.target.value); }}
                InputLabelProps={{ shrink: true }}
                sx={{ minWidth: { xs: '100%', sm: 170 } }}
              />

              <TextField
                size="small"
                type="date"
                label="Esemény dátuma (-ig)"
                value={filterDateTo}
                onChange={(e) => { setPage(1); setFilterDateTo(e.target.value); }}
                InputLabelProps={{ shrink: true }}
                inputProps={{ min: filterDateFrom || undefined }}
                sx={{ minWidth: { xs: '100%', sm: 170 } }}
              />
            </Stack>
          </Box>
        </Collapse>
      </Box>

      <List sx={{ width: '100%' }}>
        {bookings.map((booking) => {
          const PAYABLE_STATUSES = ['NOT_PAID', 'DEPOSIT_REQUESTED', 'DEPOSIT_PAID'];
          // Akkor van függőben offline megerősítés, ha fizetési mód ki van választva de az ügyfél még nem erősítette meg
          const pendingOfflineConfirmation = booking.selectedPaymentMethod && !booking.clientPaymentConfirmedAt;
          // Kosárhoz adás: APPROVED + fizetendő státusz + nincs függőben lévő megerősítés
          const showCartButtons = PAYMENT_ENABLED && isClient() && booking.status === 'APPROVED'
            && PAYABLE_STATUSES.includes(booking.paymentStatus)
            && !pendingOfflineConfirmation;
          const isBookingInCart = isInCart(booking.id);

          return (
            <Card key={booking.id} sx={{
              mb: 2,
              borderRadius: '16px',
              overflow: 'hidden',
              background: `linear-gradient(135deg, ${theme.palette.background.paper} 0%, ${theme.palette.grey[50]} 100%)`,
              border: `1px solid ${theme.palette.divider}`,
              boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
              transition: 'all 0.2s ease',
              '&:hover': {
                transform: 'translateY(-2px)',
                boxShadow: '0 8px 24px rgba(0,0,0,0.13)',
                borderColor: theme.palette.secondary.main,
              },
            }}>
              <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>

                {/* ── FEJLÉC SÁV ── */}
                <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'space-between', alignItems: 'flex-start', gap: { xs: 0.75, sm: 1 } }}>
                  {/* Esemény neve + partner neve */}
                  <Box sx={{ minWidth: 0, flex: 1, width: '100%' }}>
                    <Typography variant="subtitle1" component="div" noWrap sx={{ fontWeight: 700, lineHeight: 1.3 }}>
                      {booking.eventType}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" noWrap>
                      {isClient()
                        ? booking.provider?.businessName
                        : `${booking.client?.firstName ?? ''} ${booking.client?.lastName ?? ''}`.trim()}
                    </Typography>
                  </Box>

                  {/* Chipek + dátum – mobilon saját sorba kerül, balra igazítva */}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap', justifyContent: { xs: 'flex-start', sm: 'flex-end' }, flexShrink: 0 }}>
                    <Chip label={getStatusText(booking.status)} color={getStatusColor(booking.status)} size="small" />
                    {booking.paymentStatus && booking.paymentStatus !== 'NOT_PAID' && (
                      <Chip label={getPaymentStatusText(booking.paymentStatus)} color={getPaymentStatusColor(booking.paymentStatus)} size="small" />
                    )}
                    <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                      {format(parseISO(booking.eventDate), 'yyyy. MM. dd.', { locale: hu })}
                    </Typography>
                  </Box>
                </Box>

                {/* ── AKCIÓ SÁV ── */}
                <Box sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: { xs: 'flex-start', sm: 'center' },
                  flexWrap: 'wrap',
                  gap: 1,
                  mt: 1.5,
                }}>
                  {/* Bal: akció gombok */}
                  <Box sx={{ display: 'flex', gap: 0.75, alignItems: 'center', flexWrap: 'wrap' }}>
                    {isProvider() && (booking.status === 'PENDING' || booking.status === 'OFFER_REJECTED') && (
                      <Button size="small" variant="contained" startIcon={<Send />}
                        onClick={() => handleOpenSendOfferDialog(booking)}>
                        {booking.status === 'OFFER_REJECTED' ? 'Új ajánlat küldése' : 'Ajánlat küldése'}
                      </Button>
                    )}
                    {isProvider() && booking.status === 'OFFER_ACCEPTED' && (
                      <Button size="small" variant="contained" color="success" startIcon={<CheckCircle />}
                        onClick={() => handleApproveBooking(booking.id)}>
                        Jóváhagyás
                      </Button>
                    )}
                    {isProvider() && booking.status === 'APPROVED' && ['SUCCEEDED', 'FULL_PAID'].includes(booking.paymentStatus) &&
                      new Date(booking.eventDate).setHours(0,0,0,0) <= new Date().setHours(0,0,0,0) && (
                      <Button size="small" variant="contained" color="success" startIcon={<DoneAll />}
                        onClick={() => handleOpenMarkCompletedDialog(booking)}>
                        Befejezve
                      </Button>
                    )}
                    {/* Provider: fizetés beérkezésének megerősítése — a kliens megerősítése után, vagy
                        elsőként is (bármelyik fél megerősítése elég), ha a kliens offline módot választott */}
                    {isProvider() && booking.status === 'APPROVED' && !booking.providerPaymentConfirmedAt
                      && (booking.clientPaymentConfirmedAt || (booking.selectedPaymentMethod && booking.selectedPaymentMethod !== 'BARION')) && (() => {
                      const isDepositStep = booking.clientPaymentConfirmedAt
                        ? booking.paymentStatus === 'DEPOSIT_PAID'
                        : booking.paymentStatus === 'DEPOSIT_REQUESTED';
                      return (
                        <Button size="small" variant="outlined" color="success" startIcon={<CheckCircle />}
                          onClick={() => setConfirmPaymentDialog({
                            open: true,
                            bookingId: booking.id,
                            role: 'PROVIDER',
                            step: isDepositStep ? 'DEPOSIT' : 'FULL',
                          })}>
                          {isDepositStep ? 'Előleg beérkezésének megerősítése' : 'Teljes összeg beérkezésének megerősítése'}
                        </Button>
                      );
                    })()}
                    {/* Client: kosár gomb APPROVED + fizetendő + még nincs offline fizetés elindítva */}
                    {showCartButtons && (
                      !isBookingInCart ? (
                        <Button size="small" variant="contained" color="primary" startIcon={<ShoppingCart />}
                          onClick={() => { addToCart(booking.id); setCartSnackbarOpen(true); }}>
                          {booking.paymentStatus === 'DEPOSIT_REQUESTED'
                            ? (isMobile ? 'Előleg' : 'Előleg kosárhoz')
                            : booking.paymentStatus === 'DEPOSIT_PAID'
                              ? (isMobile ? 'Fennmaradó összeg' : 'Fennmaradó összeg kosárhoz')
                              : 'Kosárhoz adás'}
                        </Button>
                      ) : (
                        <Button size="small" variant="outlined" color="error" startIcon={<ShoppingCart />}
                          onClick={() => removeFromCart(booking.id)}>
                          Kosárból eltávolítás
                        </Button>
                      )
                    )}
                    {/* Client: fizetési mód kiválasztása (offline vagy Barion) – PAYMENT_ENABLED-tól független */}
                    {isClient() && booking.status === 'APPROVED'
                      && PAYABLE_STATUSES.includes(booking.paymentStatus)
                      && !booking.selectedPaymentMethod
                      && !isBookingInCart
                      && (booking.provider?.paymentMethods || []).length > 0 && (
                      <Button
                        size="small"
                        variant="outlined"
                        color="secondary"
                        startIcon={<Payment />}
                        onClick={() => {
                          const step = booking.paymentStatus === 'DEPOSIT_REQUESTED' ? 'DEPOSIT' : 'FULL';
                          setPaymentMethodDialog({ open: true, bookings: [booking], step });
                        }}
                      >
                        {booking.paymentStatus === 'DEPOSIT_REQUESTED' ? 'Előleg fizetési módja' : 'Fizetési mód kiválasztása'}
                      </Button>
                    )}
                    {/* Client: jóváhagyott, fizetendő, de a szolgáltató még nem adott meg fizetési módot —
                        korábban semmi nem jelent meg, a foglalás "Fizetésre vár" állapotban ragadt */}
                    {isClient() && booking.status === 'APPROVED'
                      && PAYABLE_STATUSES.includes(booking.paymentStatus)
                      && !booking.selectedPaymentMethod
                      && !isBookingInCart
                      && !showCartButtons
                      && (booking.provider?.paymentMethods || []).length === 0 && (
                      <Typography variant="body2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <InfoOutlined fontSize="small" sx={{ color: 'info.main' }} />
                        A fizetés módjáról egyeztessen a szolgáltatóval üzenetben.
                      </Typography>
                    )}
                    {/* Client: elindított online fizetés, a Barion visszaigazolására várunk */}
                    {isClient() && booking.status === 'APPROVED' && booking.paymentStatus === 'PENDING' && (
                      <Typography variant="body2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <InfoOutlined fontSize="small" sx={{ color: 'info.main' }} />
                        Az online fizetés elindult, a visszaigazolásra várunk.
                      </Typography>
                    )}
                    {/* Client: offline fizetés elindítva, várakozik megerősítésre */}
                    {isClient() && booking.status === 'APPROVED' && booking.selectedPaymentMethod && !booking.clientPaymentConfirmedAt && !booking.providerPaymentConfirmedAt && (
                      <>
                        <Chip
                          size="small"
                          label={getPaymentMethodLabel(booking.selectedPaymentMethod)}
                          color="info"
                          variant="outlined"
                        />
                        <Button size="small" variant="outlined" color="success" startIcon={<CheckCircle />}
                          onClick={() => {
                            const step = booking.paymentStatus === 'DEPOSIT_REQUESTED' ? 'DEPOSIT' : 'FULL';
                            setConfirmPaymentDialog({ open: true, bookingId: booking.id, role: 'CLIENT', step });
                          }}>
                          Fizetés megerősítése
                        </Button>
                      </>
                    )}
                    {isClient() && booking.status === 'OFFER_SENT' && (
                      <Button size="small" variant="contained" color="success" startIcon={<CheckCircle />}
                        onClick={() => handleAcceptOffer(booking.id)}>
                        Elfogad
                      </Button>
                    )}
                    {isClient() && booking.status === 'OFFER_SENT' && (
                      <>
                        <Button size="small" variant="outlined" color="error" startIcon={<Cancel />}
                          onClick={() => handleOpenRejectOfferDialog(booking)}>
                          Elutasít
                        </Button>
                        <Button size="small" variant="outlined" color="primary" startIcon={<Reply />}
                          onClick={() => handleOpenRequestNewOfferDialog(booking)}>
                          Ellenajánlat
                        </Button>
                      </>
                    )}
                  </Box>

                  {/* Jobb: üzenet + részletek */}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
                    <IconButton
                      size="small"
                      title="Üzenet küldése"
                      aria-label="Üzenet küldése"
                      sx={{ width: 40, height: 40 }}
                      onClick={() => {
                        const participant = isClient() ? booking.provider?.user : booking.client;
                        if (participant?.id) navigate('/messages', { state: { autoOpen: participant } });
                      }}
                    >
                      <Message fontSize="small" />
                    </IconButton>
                    <Button
                      size="small"
                      onClick={() => handleExpandClick(booking.id)}
                      endIcon={<ExpandMore sx={{ transform: expandedBookingId === booking.id ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s' }} />}
                    >
                      {expandedBookingId === booking.id ? 'Kevesebb' : 'Részletek'}
                    </Button>
                  </Box>
                </Box>

                {/* ── RÉSZLETEK (Collapse) ── */}
                <Collapse in={expandedBookingId === booking.id} timeout="auto" unmountOnExit>
                  <Box sx={{
                    mt: 1.5,
                    pt: 1.5,
                    mx: -2,
                    px: 2,
                    pb: 2,
                    bgcolor: alpha(theme.palette.grey[100], 0.6),
                    borderRadius: '0 0 12px 12px',
                  }}>

                    {/* Műveletek */}
                    {(booking.formResponseId ||
                      (booking.status !== 'COMPLETED' && booking.status !== 'CANCELLED')) && (
                      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center', mb: 1.5 }}>
                        {booking.formResponseId && (
                          <Button size="small" variant="outlined" startIcon={<Description />}
                            onClick={() => handleViewFormResponse(booking.formResponseId)}>
                            Kérdőív válaszai
                          </Button>
                        )}
                        {/* Lemondás: feliratos, visszafogott gomb a sor végén (korábban felirat nélküli
                            piros × volt, csak tooltippel — telefonon nem derült ki, mit csinál) */}
                        {booking.status !== 'COMPLETED' && booking.status !== 'CANCELLED' && (
                          <Button size="small" variant="outlined" color="error" startIcon={<Close />}
                            onClick={() => handleOpenCancelBookingDialog(booking)}
                            sx={{ ml: 'auto' }}>
                            {isClient() ? 'Foglalás lemondása' : 'Lemondás'}
                          </Button>
                        )}
                      </Box>
                    )}

                    {/* Esemény adatai */}
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700, display: 'flex', alignItems: 'center', gap: 0.5, mb: 1, mt: 0.5 }}>
                      <Event sx={{ fontSize: 13 }} /> Esemény adatai
                    </Typography>
                    <Grid container spacing={1.5} sx={{ mb: 1.5 }}>
                      <Grid size={{ xs: 6, sm: 3 }}>
                        <Typography variant="caption" color="text.secondary" display="block">Típus</Typography>
                        <Typography variant="body2">{booking.eventType}</Typography>
                      </Grid>
                      <Grid size={{ xs: 6, sm: 3 }}>
                        <Typography variant="caption" color="text.secondary" display="block">Dátum</Typography>
                        <Typography variant="body2">
                          {format(parseISO(booking.eventDate), 'yyyy. MM. dd.', { locale: hu })}
                          {booking.eventTime && ` ${booking.eventTime}`}
                        </Typography>
                      </Grid>
                      {booking.guestCount && (
                        <Grid size={{ xs: 6, sm: 3 }}>
                          <Typography variant="caption" color="text.secondary" display="block">Vendégek</Typography>
                          <Typography variant="body2">{booking.guestCount} fő</Typography>
                        </Grid>
                      )}
                      {booking.durationHours && (
                        <Grid size={{ xs: 6, sm: 3 }}>
                          <Typography variant="caption" color="text.secondary" display="block">Időtartam</Typography>
                          <Typography variant="body2">{booking.durationHours} óra</Typography>
                        </Grid>
                      )}
                    </Grid>

                    {/* Helyszín */}
                    {booking.eventAddress && (
                      <>
                        <Divider sx={{ mb: 1.5 }} />
                        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700, display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
                          <LocationOn sx={{ fontSize: 13 }} /> Helyszín
                        </Typography>
                        <Typography variant="body2" sx={{ mb: 1.5 }}>
                          {booking.eventZipCode} {booking.eventCity}, {booking.eventAddress}
                        </Typography>
                      </>
                    )}

                    {/* Kapcsolattartó */}
                    <Divider sx={{ mb: 1.5 }} />
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700, display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
                      <Person sx={{ fontSize: 13 }} /> {isClient() ? 'Szolgáltató és kapcsolattartó' : 'Ügyfél és kapcsolattartó'}
                    </Typography>
                    <Grid container spacing={1.5} sx={{ mb: 1.5 }}>
                      <Grid size={{ xs: 6, sm: 3 }}>
                        <Typography variant="caption" color="text.secondary" display="block">{isClient() ? 'Szolgáltató' : 'Ügyfél'}</Typography>
                        <Typography variant="body2">
                          {isClient()
                            ? booking.provider?.businessName
                            : `${booking.client?.firstName ?? ''} ${booking.client?.lastName ?? ''}`.trim()}
                        </Typography>
                      </Grid>
                      <Grid size={{ xs: 6, sm: 3 }}>
                        <Typography variant="caption" color="text.secondary" display="block">Kapcsolattartó</Typography>
                        <Typography variant="body2">{booking.contactName}</Typography>
                      </Grid>
                      <Grid size={{ xs: 6, sm: 3 }}>
                        <Typography variant="caption" color="text.secondary" display="block">Email</Typography>
                        <Typography variant="body2">{booking.contactEmail}</Typography>
                      </Grid>
                      {booking.contactPhone && (
                        <Grid size={{ xs: 6, sm: 3 }}>
                          <Typography variant="caption" color="text.secondary" display="block">Telefon</Typography>
                          <Typography variant="body2">{booking.contactPhone}</Typography>
                        </Grid>
                      )}
                    </Grid>

                    {/* Pénzügyek */}
                    {(booking.estimatedPrice || booking.offerPrice || booking.finalPrice) && (
                      <>
                        <Divider sx={{ mb: 1.5 }} />
                        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700, display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
                          <Euro sx={{ fontSize: 13 }} /> Pénzügyek
                        </Typography>
                        <Grid container spacing={1.5} sx={{ mb: 1.5 }}>
                          {booking.estimatedPrice && (
                            <Grid size={{ xs: 6, sm: 4 }}>
                              <Typography variant="caption" color="text.secondary" display="block">Becsült ár</Typography>
                              <Typography variant="body2">{booking.estimatedPrice.toLocaleString()} Ft</Typography>
                            </Grid>
                          )}
                          {booking.offerPrice && (
                            <Grid size={{ xs: 6, sm: 4 }}>
                              <Typography variant="caption" color="text.secondary" display="block">Ajánlott ár</Typography>
                              <Typography variant="body2" color="primary" fontWeight="bold">
                                {parseFloat(booking.offerPrice).toLocaleString()} Ft
                              </Typography>
                            </Grid>
                          )}
                          {booking.finalPrice && (
                            <Grid size={{ xs: 6, sm: 4 }}>
                              <Typography variant="caption" color="text.secondary" display="block">Végleges ár</Typography>
                              <Typography variant="body2" color="primary" fontWeight="bold">
                                {booking.finalPrice.toLocaleString()} Ft
                              </Typography>
                            </Grid>
                          )}
                        </Grid>
                      </>
                    )}

                    {/* Üzenetek */}
                    {(booking.message || booking.offerMessage || booking.notes) && (
                      <>
                        <Divider sx={{ mb: 1.5 }} />
                        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700, display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
                          <Message sx={{ fontSize: 13 }} /> Üzenetek
                        </Typography>
                        <Grid container spacing={1.5} sx={{ mb: 1.5 }}>
                          {booking.message && (
                            <Grid size={12}>
                              <Typography variant="caption" color="text.secondary" display="block">Ügyfél üzenete</Typography>
                              <Typography variant="body2">{booking.message}</Typography>
                            </Grid>
                          )}
                          {booking.offerMessage && (
                            <Grid size={12}>
                              <Typography variant="caption" color="text.secondary" display="block">Ajánlat üzenet</Typography>
                              <Typography variant="body2">{booking.offerMessage}</Typography>
                            </Grid>
                          )}
                          {booking.notes && (
                            <Grid size={12}>
                              <Typography variant="caption" color="text.secondary" display="block">Megjegyzések</Typography>
                              <Typography variant="body2">{booking.notes}</Typography>
                            </Grid>
                          )}
                        </Grid>
                      </>
                    )}

                    {/* Létrehozva */}
                    <Divider sx={{ mb: 1 }} />
                    <Typography variant="caption" color="text.secondary">
                      Létrehozva: {format(parseISO(booking.createdAt), 'yyyy. MM. dd. HH:mm', { locale: hu })}
                    </Typography>
                  </Box>
                </Collapse>

              </CardContent>
            </Card>
          );
        })}
      </List>

      {totalPages > 1 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3, mb: 1 }}>
          <Pagination
            count={totalPages}
            page={page}
            onChange={(_, val) => {
              setPage(val);
              listTopRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }}
            color="primary"
            shape="rounded"
          />
        </Box>
      )}

      {/* Ajánlat küldése Dialog */}
      <Dialog
        open={sendOfferDialogOpen}
        onClose={() => setSendOfferDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle sx={{ p: 0 }}>
          <DialogHeader
            icon={<Send />}
            title="Ajánlat küldése"
            subtitle={selectedBookingForAction ? `${selectedBookingForAction.client?.firstName} ${selectedBookingForAction.client?.lastName}` : undefined}
          />
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 2 }}>
            Kérdés: {selectedBookingForAction?.message || 'Nincs üzenet'}
          </Typography>
          <TextField
            autoFocus
            margin="dense"
            label="Ajánlott ár (Ft)"
            type="number"
            required
            fullWidth
            variant="outlined"
            value={offerPrice}
            onChange={(e) => setOfferPrice(e.target.value)}
            inputProps={{ min: 0, inputMode: 'numeric' }}
            error={!!offerFieldError('price')}
            helperText={offerFieldError('price') || (Number(offerPrice) > 0 ? `${Number(offerPrice).toLocaleString('hu-HU')} Ft` : undefined)}
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            label="Üzenet az ajánlathoz (opcionális)"
            multiline
            rows={3}
            fullWidth
            variant="outlined"
            value={offerMessage}
            onChange={(e) => setOfferMessage(e.target.value.slice(0, 1000))}
            helperText={`${offerMessage.length}/1000 karakter`}
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            label="Ajánlat érvényessége (dátum, opcionális)"
            type="date"
            fullWidth
            variant="outlined"
            InputLabelProps={{ shrink: true }}
            inputProps={{ min: new Date().toISOString().split('T')[0] }}
            value={offerExpiresAt}
            onChange={(e) => setOfferExpiresAt(e.target.value)}
            error={!!offerFieldError('expiresAt')}
            helperText={offerFieldError('expiresAt')}
            sx={{ mb: 2 }}
          />
          <Divider sx={{ my: 2 }} />
          <Typography variant="subtitle2" fontWeight={600} gutterBottom>
            Előleg és fizetési határidők
          </Typography>
          <TextField
            margin="dense"
            label="Előleg összege (Ft, opcionális)"
            type="number"
            fullWidth
            variant="outlined"
            value={offerDepositAmount}
            onChange={(e) => setOfferDepositAmount(e.target.value)}
            inputProps={{ min: 0, inputMode: 'numeric' }}
            error={!!offerFieldError('deposit')}
            helperText={offerFieldError('deposit') || 'Ha nem kér előleget, hagyja üresen'}
            sx={{ mb: 2 }}
          />
          {offerDepositAmount !== '' && (
            <TextField
              margin="dense"
              label="Előleg fizetési határideje"
              type="date"
              fullWidth
              variant="outlined"
              InputLabelProps={{ shrink: true }}
              inputProps={{ min: new Date().toISOString().split('T')[0] }}
              value={offerDepositDeadline}
              onChange={(e) => setOfferDepositDeadline(e.target.value)}
              required
              error={!!offerFieldError('depositDeadline')}
              helperText={offerFieldError('depositDeadline')}
              sx={{ mb: 2 }}
            />
          )}
          <TextField
            margin="dense"
            label="Teljes összeg fizetési határideje"
            type="date"
            fullWidth
            variant="outlined"
            InputLabelProps={{ shrink: true }}
            value={offerFullPaymentDeadline}
            onChange={(e) => setOfferFullPaymentDeadline(e.target.value)}
            inputProps={{ min: todayStr, max: eventDateStr || undefined }}
            error={!!offerFieldError('fullDeadline')}
            helperText={offerFieldError('fullDeadline') || 'Ajánlott: az esemény előtti néhány napig'}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2.5, gap: 1, borderTop: '1px solid', borderTopColor: 'divider' }}>
          <Button onClick={() => setSendOfferDialogOpen(false)} disabled={actionLoading}>
            Mégse
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleSendOffer}
            disabled={actionLoading}
            startIcon={actionLoading ? <CircularProgress size={20} /> : <Send />}
          >
            Ajánlat elküldése
          </Button>
        </DialogActions>
      </Dialog>

      {/* Ajánlat elutasítása Dialog */}
      <Dialog
        open={rejectOfferDialogOpen}
        onClose={() => setRejectOfferDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ p: 0 }}>
          <DialogHeader
            icon={<Cancel />}
            title="Ajánlat elutasítása"
            subtitle={selectedBookingForAction?.provider?.businessName}
          />
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 2 }}>
            Biztosan el szeretné utasítani az ajánlatot?
          </Typography>
          <TextField
            autoFocus
            margin="dense"
            label="Indok (opcionális)"
            multiline
            rows={3}
            fullWidth
            variant="outlined"
            value={rejectOfferReason}
            onChange={(e) => setRejectOfferReason(e.target.value)}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2.5, gap: 1, borderTop: '1px solid', borderTopColor: 'divider' }}>
          <Button onClick={() => setRejectOfferDialogOpen(false)} disabled={actionLoading}>
            Mégse
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleRejectOffer}
            disabled={actionLoading}
            startIcon={actionLoading ? <CircularProgress size={20} /> : <Cancel />}
          >
            Elutasítás
          </Button>
        </DialogActions>
      </Dialog>

      {/* Új ajánlat kérése Dialog */}
      <Dialog
        open={requestNewOfferDialogOpen}
        onClose={() => setRequestNewOfferDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ p: 0 }}>
          <DialogHeader
            icon={<Reply />}
            title="Új ajánlat kérése"
            subtitle={selectedBookingForAction?.provider?.businessName}
          />
        </DialogTitle>
        <DialogContent sx={{ pt: 2.5, pb: 1 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Jelenlegi ajánlat: <strong>{selectedBookingForAction?.offerPrice ? `${parseFloat(selectedBookingForAction.offerPrice).toLocaleString('hu-HU')} Ft` : 'Nincs'}</strong>
          </Typography>
          <TextField
            autoFocus
            margin="dense"
            label="Javasolt összeg (Ft)"
            type="number"
            fullWidth
            variant="outlined"
            value={requestNewOfferPrice}
            onChange={(e) => setRequestNewOfferPrice(e.target.value)}
            inputProps={{ min: 0, step: 1000, inputMode: 'numeric' }}
            error={newOfferSubmitAttempted && !!newOfferError}
            sx={{ mb: 1.5 }}
          />
          <TextField
            margin="dense"
            label="Üzenet a szolgáltatónak"
            multiline
            rows={3}
            fullWidth
            variant="outlined"
            value={requestNewOfferMessage}
            onChange={(e) => setRequestNewOfferMessage(e.target.value)}
            error={newOfferSubmitAttempted && !!newOfferError}
            helperText={(newOfferSubmitAttempted && newOfferError) || 'Legalább az összeg vagy az üzenet megadása kötelező.'}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2.5, gap: 1, borderTop: '1px solid', borderTopColor: 'divider' }}>
          <Button onClick={() => { setRequestNewOfferDialogOpen(false); setRequestNewOfferPrice(''); }} disabled={actionLoading}>
            Mégse
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleRequestNewOffer}
            disabled={actionLoading}
            startIcon={actionLoading ? <CircularProgress size={20} /> : <Reply />}
          >
            Ellenajánlat elküldése
          </Button>
        </DialogActions>
      </Dialog>

      {/* Foglalás lemondása Dialog */}
      <Dialog
        open={cancelBookingDialogOpen}
        onClose={() => setCancelBookingDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ p: 0 }}>
          <DialogHeader
            icon={<Cancel />}
            title="Foglalás lemondása"
            subtitle={selectedBookingForAction?.eventType}
          />
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 2 }}>
            Biztosan le szeretné mondani ezt a foglalást?
          </Typography>
          <TextField
            autoFocus
            margin="dense"
            label="Indok (opcionális)"
            multiline
            rows={3}
            fullWidth
            variant="outlined"
            value={cancelBookingReason}
            onChange={(e) => setCancelBookingReason(e.target.value)}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2.5, gap: 1, borderTop: '1px solid', borderTopColor: 'divider' }}>
          <Button onClick={() => setCancelBookingDialogOpen(false)} disabled={actionLoading}>
            Mégse
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleCancelBooking}
            disabled={actionLoading}
            startIcon={actionLoading ? <CircularProgress size={20} /> : <Cancel />}
          >
            Lemondás
          </Button>
        </DialogActions>
      </Dialog>

      {/* ÚJ: Foglalás befejezetté jelölése Dialog */}
      <Dialog
        open={markCompletedDialogOpen}
        onClose={() => setMarkCompletedDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ p: 0 }}>
          <DialogHeader
            icon={<DoneAll />}
            title="Foglalás befejezetté jelölése"
            subtitle={selectedBookingForAction?.eventType}
          />
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 2 }}>
            Biztosan befejezetté szeretné jelölni ezt a foglalást? Ez a művelet nem vonható vissza.
          </Typography>
          <TextField
            autoFocus
            margin="dense"
            label="Megjegyzések (opcionális)"
            multiline
            rows={3}
            fullWidth
            variant="outlined"
            value={markCompletedNotes}
            onChange={(e) => setMarkCompletedNotes(e.target.value)}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2.5, gap: 1, borderTop: '1px solid', borderTopColor: 'divider' }}>
          <Button onClick={() => setMarkCompletedDialogOpen(false)} disabled={actionLoading}>
            Mégse
          </Button>
          <Button
            variant="contained"
            color="success"
            onClick={handleMarkAsCompleted}
            disabled={actionLoading}
            startIcon={actionLoading ? <CircularProgress size={20} /> : <DoneAll />}
          >
            Befejezetté jelölés
          </Button>
        </DialogActions>
      </Dialog>

      {/* ÚJ: FormResponseViewer Dialog */}
      <Dialog
        open={formResponseViewerOpen}
        onClose={() => setFormResponseViewerOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ p: 0 }}>
          <DialogHeader
            icon={<Description />}
            title="Ajánlatkérő kérdőív válaszai"
            onClose={() => setFormResponseViewerOpen(false)}
          />
        </DialogTitle>
        <DialogContent>
          {formResponseIdToView && (
            <FormResponseViewer formResponseId={formResponseIdToView} />
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2.5, gap: 1, borderTop: '1px solid', borderTopColor: 'divider' }}>
          <Button onClick={() => setFormResponseViewerOpen(false)}>Bezár</Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={snackbarDetail ? 8000 : 6000}
        onClose={() => { setSnackbarOpen(false); setSnackbarDetail(''); }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        sx={{ maxWidth: 520 }}
      >
        <Alert onClose={() => { setSnackbarOpen(false); setSnackbarDetail(''); }} severity={snackbarSeverity} sx={{ width: '100%' }}>
          {snackbarDetail ? (
            <>
              <AlertTitle sx={{ fontWeight: 600, mb: 0.5 }}>{snackbarMessage}</AlertTitle>
              {snackbarDetail}
            </>
          ) : snackbarMessage}
        </Alert>
      </Snackbar>

      {/* Kosárhoz adás snackbar */}
      <Snackbar
        open={cartSnackbarOpen}
        autoHideDuration={5000}
        onClose={() => setCartSnackbarOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity="success"
          onClose={() => setCartSnackbarOpen(false)}
          action={
            <Button color="inherit" size="small" onClick={() => { setCartSnackbarOpen(false); navigate('/kosar'); }}>
              Ugrás a kosárhoz
            </Button>
          }
          sx={{ width: '100%' }}
        >
          Hozzáadva a kosárhoz!
        </Alert>
      </Snackbar>

      {/* Offline fizetési mód kiválasztása dialog */}
      <PaymentMethodDialog
        open={paymentMethodDialog.open}
        onClose={() => setPaymentMethodDialog({ open: false, bookings: [], step: 'FULL' })}
        bookings={paymentMethodDialog.bookings}
        paymentStep={paymentMethodDialog.step}
        onSuccess={() => {
          setPaymentMethodDialog({ open: false, bookings: [], step: 'FULL' });
          fetchBookings();
        }}
      />

      {/* Fizetés megerősítése dialog */}
      <Dialog open={confirmPaymentDialog.open} onClose={() => setConfirmPaymentDialog(d => ({ ...d, open: false }))} maxWidth="xs" fullWidth>
        <DialogTitle>
          {confirmPaymentDialog.role === 'CLIENT'
            ? (confirmPaymentDialog.step === 'DEPOSIT' ? 'Előleg fizetésének megerősítése' : 'Teljes összeg fizetésének megerősítése')
            : (confirmPaymentDialog.step === 'DEPOSIT' ? 'Előleg beérkezésének megerősítése' : 'Teljes összeg beérkezésének megerősítése')}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1">
            {confirmPaymentDialog.role === 'CLIENT'
              ? `Megerősíti, hogy a(z) ${confirmPaymentDialog.step === 'DEPOSIT' ? 'előleg' : 'teljes összeg'} kifizetése megtörtént?`
              : `Megerősíti, hogy a(z) ${confirmPaymentDialog.step === 'DEPOSIT' ? 'előleg' : 'teljes összeg'} beérkezett a számlájára?`}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
          <Button onClick={() => setConfirmPaymentDialog(d => ({ ...d, open: false }))} disabled={confirmPaymentLoading}>
            Mégse
          </Button>
          <Button variant="contained" color="success" onClick={handleConfirmPayment} disabled={confirmPaymentLoading}
            startIcon={confirmPaymentLoading ? <CircularProgress size={18} /> : <CheckCircle />}>
            Megerősítés
          </Button>
        </DialogActions>
      </Dialog>

    </Box>
  );
};

export default BookingsList;